import { useCallback, useEffect, useRef } from 'react';
import {
  ProlongIntentKind,
  PROLONG_NOTIFICATION_PARAM,
  readProlongIntent,
  removeQueryParams,
  showNotificationAndRemoveParams,
} from '@pkgs/components/utils/prolongSearchAlertUtils';
import { useAppContext } from '@pkgs/components/hooks/useAppContext';
import { useStatusMessage } from '@ev-tech/ui-core-components/StatusMessageProvider';
import { type AlertVariants, alertVariants } from '@ev-tech/ui-core-components/Alert';

export const useProlongSearchAlert = () => {
  const { api, t } = useAppContext();
  const { showMessage } = useStatusMessage();
  const hasStarted = useRef(false);

  const showProlongNotificationMessage = useCallback(
    (notification: AlertVariants) => {
      showMessage(
        t(
          notification === alertVariants.success
            ? 'search.userAccount.searchAlert.prolong.success'
            : 'search.userAccount.searchAlert.prolong.error',
        ),
        { alertType: notification },
      );
    },
    [t, showMessage],
  );

  const prolongSearchAlert = async (alertId: string, authKey: string) => {
    const result = await api.getSearchAlertForDisplay(alertId, { authKey, prolong: true });

    if (result.status !== 'success') {
      throw result.error;
    }
    return result.data;
  };

  useEffect(() => {
    if (hasStarted.current) {
      return;
    }

    const query = new URLSearchParams(window.location.search);
    const intent = readProlongIntent(query);

    if (intent.kind === ProlongIntentKind.NONE) {
      return;
    }

    hasStarted.current = true;

    if (intent.kind === ProlongIntentKind.SHOW_NOTIFICATION) {
      showProlongNotificationMessage(intent.notification);
      removeQueryParams(query, [PROLONG_NOTIFICATION_PARAM]);
      return;
    }

    prolongSearchAlert(intent.alertId, intent.authKey)
      .then(() => showNotificationAndRemoveParams(alertVariants.success, showProlongNotificationMessage))
      .catch(() => showNotificationAndRemoveParams(alertVariants.error, showProlongNotificationMessage));
  }, []);
};
